import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Save, Loader2, Plus, X, Trash2 } from "lucide-react";

const API = "/api";

const SOCIAL_PLATFORMS = [
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "youtube", label: "YouTube" },
  { id: "tiktok", label: "TikTok" },
  { id: "x", label: "X (Twitter)" },
  { id: "whatsapp", label: "WhatsApp" },
];

const FooterEditor = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/footer`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/footer`, data);
      setData(res.data);
      toast.success("Footer salvato!");
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const updateSocial = (platform, url) => {
    const socials = [...(data.socials || [])];
    const idx = socials.findIndex((s) => s.platform === platform);
    if (idx >= 0) {
      socials[idx] = { platform, url };
    } else {
      socials.push({ platform, url });
    }
    setData({ ...data, socials });
  };

  const getSocialUrl = (platform) => {
    return (data.socials || []).find((s) => s.platform === platform)?.url || "";
  };

  const updateColumn = (colIdx, field, value) => {
    const columns = [...(data.columns || [])];
    columns[colIdx] = { ...columns[colIdx], [field]: value };
    setData({ ...data, columns });
  };

  const updateColumnLink = (colIdx, linkIdx, field, value) => {
    const columns = [...(data.columns || [])];
    const links = [...(columns[colIdx].links || [])];
    links[linkIdx] = { ...links[linkIdx], [field]: value };
    columns[colIdx] = { ...columns[colIdx], links };
    setData({ ...data, columns });
  };

  const addColumnLink = (colIdx) => {
    const columns = [...(data.columns || [])];
    columns[colIdx] = { ...columns[colIdx], links: [...(columns[colIdx].links || []), { label: "", href: "" }] };
    setData({ ...data, columns });
  };

  const removeColumnLink = (colIdx, linkIdx) => {
    const columns = [...(data.columns || [])];
    columns[colIdx] = { ...columns[colIdx], links: columns[colIdx].links.filter((_, i) => i !== linkIdx) };
    setData({ ...data, columns });
  };

  const addColumn = () => {
    setData({ ...data, columns: [...(data.columns || []), { title: "Nuova Colonna", links: [] }] });
  };

  const removeColumn = (colIdx) => {
    setData({ ...data, columns: (data.columns || []).filter((_, i) => i !== colIdx) });
  };

  if (!data) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>;

  return (
    <div data-testid="admin-footer-editor">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          Editor Footer
        </h2>
        <button onClick={save} disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-5 py-2.5 text-sm btn-scale flex items-center gap-2 disabled:opacity-50"
          data-testid="footer-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva Footer
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-100 p-5 space-y-4" data-testid="footer-info-section">
          <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>Informazioni Azienda</h3>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Descrizione</label>
            <textarea value={data.description || ""} onChange={(e) => setData({ ...data, description: e.target.value })}
              rows={3} className="w-full rounded-xl border border-input px-3 py-2 text-sm resize-none" data-testid="footer-description" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Email</label>
              <Input value={data.email || ""} onChange={(e) => setData({ ...data, email: e.target.value })}
                placeholder="info@azienda.it" className="h-10 rounded-xl text-sm" data-testid="footer-email" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Telefono</label>
              <Input value={data.phone || ""} onChange={(e) => setData({ ...data, phone: e.target.value })}
                placeholder="+39 02 1234 5678" className="h-10 rounded-xl text-sm" data-testid="footer-phone" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Indirizzo</label>
            <Input value={data.address || ""} onChange={(e) => setData({ ...data, address: e.target.value })}
              placeholder="Via Roma 1, Milano" className="h-10 rounded-xl text-sm" data-testid="footer-address" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Copyright</label>
            <Input value={data.copyright_text || ""} onChange={(e) => setData({ ...data, copyright_text: e.target.value })}
              className="h-10 rounded-xl text-sm" data-testid="footer-copyright" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 p-5 space-y-3" data-testid="footer-social-section">
          <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>Link Social</h3>
          <p className="text-[11px] text-slate-400">Inserisci l'URL completo per ogni social. Lascia vuoto per nasconderlo.</p>
          {SOCIAL_PLATFORMS.map((p) => (
            <div key={p.id} className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-600 w-24 shrink-0">{p.label}</span>
              <Input
                value={getSocialUrl(p.id)}
                onChange={(e) => updateSocial(p.id, e.target.value)}
                placeholder={`https://${p.id === "whatsapp" ? "wa.me/39..." : p.id + ".com/..."}`}
                className="h-9 rounded-lg text-xs flex-1"
                data-testid={`footer-social-${p.id}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl border border-slate-100 p-5" data-testid="footer-columns-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>Colonne Link</h3>
          <button onClick={addColumn}
            className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-emerald-700"
            data-testid="footer-add-column">
            <Plus className="w-3.5 h-3.5" /> Aggiungi colonna
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {(data.columns || []).map((col, ci) => (
            <div key={ci} className="bg-slate-50 rounded-xl p-4 space-y-3" data-testid={`footer-column-${ci}`}>
              <div className="flex items-center gap-2">
                <Input value={col.title} onChange={(e) => updateColumn(ci, "title", e.target.value)}
                  className="h-9 rounded-lg text-sm font-bold flex-1" />
                <button onClick={() => removeColumn(ci)}
                  className="text-red-400 hover:text-red-600 p-1" data-testid={`footer-remove-column-${ci}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {(col.links || []).map((link, li) => (
                <div key={li} className="flex items-center gap-1.5">
                  <Input value={link.label} onChange={(e) => updateColumnLink(ci, li, "label", e.target.value)}
                    placeholder="Label" className="h-8 rounded-lg text-xs flex-1" />
                  <Input value={link.href} onChange={(e) => updateColumnLink(ci, li, "href", e.target.value)}
                    placeholder="/link" className="h-8 rounded-lg text-xs flex-1 font-mono" />
                  <button onClick={() => removeColumnLink(ci, li)}
                    className="text-slate-300 hover:text-red-500 shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button onClick={() => addColumnLink(ci)}
                className="text-[11px] font-semibold text-emerald-600 hover:text-emerald-700"
                data-testid={`footer-add-link-${ci}`}>
                + Aggiungi link
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl border border-slate-100 p-5" data-testid="footer-regions-section">
        <h3 className="text-sm font-bold text-slate-800 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>Regioni Operative</h3>
        <textarea
          value={(data.regions || []).join(", ")}
          onChange={(e) => setData({ ...data, regions: e.target.value.split(",").map((r) => r.trim()).filter(Boolean) })}
          rows={2}
          placeholder="Lombardia, Lazio, Campania..."
          className="w-full rounded-xl border border-input px-3 py-2 text-sm resize-none"
          data-testid="footer-regions"
        />
        <p className="text-[10px] text-slate-400 mt-1">Separa le regioni con una virgola</p>
      </div>
    </div>
  );
};

export default FooterEditor;
